'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@shared/auth';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { emptyManualAddressValues } from '../../../form-controls/Address/constants';
import { MultistepForm } from '../../../form-controls/MultistepForm/MultistepForm';
import { useStepTracking } from '../../hooks/useStepTracking';
import { MyAccountSheet } from '../../MyAccountSheet/MyAccountSheet';
import { createAddressEditFormSchema, type AddressEditFormData } from '../addressEditSchema';
import { useProfileEditSheet } from '../hooks/useProfileEditSheet';
import { SuccessStep } from '../steps/SuccessStep';
import type { AddressEditSheetProps } from '../types';

import { AddressEditStep } from '../steps/AddressEditStep';
import { BackHandler } from './BackHandler';

function AddressEditSheet({ open, onOpenChange, onSave, trigger }: AddressEditSheetProps) {
  const t = useTranslations('my_account');
  const tForm = useTranslations('form');
  const [isFirstStep, setIsFirstStep] = useState(true);
  const { handleBackNavigation, registerBackHandler } = useProfileEditSheet();
  const formSchema = useMemo(() => createAddressEditFormSchema(tForm), [tForm]);

  const form = useForm<AddressEditFormData>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      address: { ...emptyManualAddressValues },
      addressLookup: '',
    },
  });

  const { session } = useSession();
  const playerProfile = session.profile;

  const handleClose = () => {
    form.reset({
      address: { ...emptyManualAddressValues },
      addressLookup: '',
    });
    setIsFirstStep(true);
    onOpenChange(false);
  };

  const handleComplete = () => {
    handleClose();
  };

  const handleBackClick = () => {
    handleBackNavigation(handleClose);
  };

  const steps = [
    {
      id: 'address-edit',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <AddressEditStep onSave={onSave} isSheetOpen={open} />
        </>
      ),
      schema: formSchema,
      'data-track-container': 'Edit Address',
    },
    {
      id: 'success',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <SuccessStep onClose={handleClose} firstName={playerProfile?.firstName || ''} type="address" />
        </>
      ),
      schema: z.object({}),
      'data-track-container': 'Identity Submitted',
    },
  ];

  const { currentTrackContainer, onStepChange } = useStepTracking(steps);

  const handleStepChange = (currentIsFirstStep: boolean, currentIsLastStep: boolean, stepId: string) => {
    setIsFirstStep(currentIsFirstStep);
    onStepChange(currentIsFirstStep, currentIsLastStep, stepId);
  };

  return (
    <MyAccountSheet
      trigger={trigger}
      title={t('address_edit.title')}
      open={open}
      onOpenChange={onOpenChange}
      showBackButton={!isFirstStep}
      onBack={handleBackClick}
      data-track-feature="My Account"
      data-track-container={currentTrackContainer}
    >
      <FormProvider {...form}>
        <MultistepForm
          steps={steps}
          showProgressBar={false}
          showNavigation={false}
          onComplete={handleComplete}
          onStepChange={handleStepChange}
          className="flex flex-col flex-1 min-h-0 h-full"
        />
      </FormProvider>
    </MyAccountSheet>
  );
}

export { AddressEditSheet };
